import { capitalize, isEmpty, orderBy } from "lodash";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import LoadingIcon from "../../../../components/icons/LoadingIcon";
import {
  renderPercentileClassification,
  useStats
} from "../../../../context/stats/StatsProvider";
import classNames from "../../../../utils/classNames";
import { formatDateToLocale } from "../../../../utils/dates";
import { ClassificationValue } from "../../../../utils/hotspotUtils";
import { SuperSimulation } from "../../../../utils/types";
import useMapConfig from "../../../../utils/useMapConfig";
import usePrevious from "../../../../utils/usePrevious";
import TablePagination from "../../../Workspace/common/TablePagination";
import usePagination, {
  PaginationKey
} from "../../../Workspace/common/usePagination";

interface Props {
  className?: string;
  superSimulations: SuperSimulation[];
}

const SuperSimulationsList: React.FC<Props> = ({
  className,
  superSimulations
}) => {
  const { getPercentile } = useStats();
  const { search: queryParamsString } = useLocation();
  const renderDistance = useMapConfig((state) => state.renderDistance);
  const containerRef = useRef<HTMLDivElement>(null);

  const sortedSuperSimulations = useMemo(
    () =>
      orderBy(
        superSimulations,
        [(superSimulation) => moment(superSimulation.created_at).valueOf()],
        ["desc"]
      ),
    [superSimulations]
  );

  const { startIndex, endIndex } = usePagination(
    useCallback(
      (state) =>
        state.getPaginationInfo(
          PaginationKey.locationSuperSimulations,
          sortedSuperSimulations.length,
          queryParamsString
        ),
      [sortedSuperSimulations.length, queryParamsString]
    )
  );

  const prevSortedSuperSimulations = usePrevious(sortedSuperSimulations);
  useEffect(() => {
    if (!containerRef.current) return;

    if (
      sortedSuperSimulations.length > prevSortedSuperSimulations.length ||
      sortedSuperSimulations.length === 1
    ) {
      setTimeout(() => {
        containerRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 10);
    }
  }, [prevSortedSuperSimulations.length, sortedSuperSimulations.length]);

  if (isEmpty(sortedSuperSimulations)) {
    return (
      <p className="mt-2 text-sm text-gray-500 dark:text-gray-50">
        There is no history of super simulations for this location.
      </p>
    );
  }

  return (
    <div ref={containerRef} className={classNames("flex flex-col", className)}>
      <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div className="shadow overflow-hidden sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-500">
              <thead className="bg-gray-50 text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    Status
                  </th>

                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    Height
                  </th>

                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    PoC score
                  </th>

                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    Visible hotspots
                  </th>

                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    Created at
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 text-gray-500 dark:text-gray-50 dark:divide-gray-500 dark:bg-gray-800">
                {sortedSuperSimulations
                  .slice(startIndex, endIndex)
                  .map((superSimulation) => (
                    <tr key={superSimulation.uuid}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {renderSuperSimulationStatusButton(superSimulation)}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {renderDistance(superSimulation.height, false)}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {superSimulation.simulation_report?.ps != null ? (
                          <ClassificationValue
                            value={
                              (getPercentile(
                                "poc_score",
                                superSimulation.simulation_report.ps || 0
                              ) || 0) / 100
                            }
                            alternativeContent={
                              getPercentile(
                                "poc_score",
                                superSimulation.simulation_report.ps
                              ) != null
                                ? `${capitalize(
                                    renderPercentileClassification(
                                      getPercentile(
                                        "poc_score",
                                        superSimulation.simulation_report.ps
                                      )
                                    )
                                  )} (${superSimulation.simulation_report.ps.toLocaleString(
                                    undefined,
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2
                                    }
                                  )})`
                                : "Loading..."
                            }
                            withoutTransmitScaleTooltip
                          />
                        ) : (
                          "-"
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {superSimulation.simulation_report ? (
                          <ClassificationValue
                            value={
                              (getPercentile(
                                "witnesses",
                                superSimulation.simulation_report.vhc || 0
                              ) || 0) / 100
                            }
                            alternativeContent={
                              getPercentile(
                                "witnesses",
                                superSimulation.simulation_report.vhc
                              ) != null
                                ? `${capitalize(
                                    renderPercentileClassification(
                                      getPercentile(
                                        "witnesses",
                                        superSimulation.simulation_report.vhc
                                      )
                                    )
                                  )} (${superSimulation.simulation_report.vhc.toLocaleString(
                                    undefined
                                  )})`
                                : "Loading..."
                            }
                            withoutTransmitScaleTooltip
                          />
                        ) : (
                          "-"
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span
                          className="text-sm text-gray-500 font-medium dark:text-gray-50"
                          title={formatDateToLocale(
                            superSimulation.created_at,
                            true
                          )}
                        >
                          {moment(superSimulation.created_at).fromNow()}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            <TablePagination
              paginationKey={PaginationKey.locationSuperSimulations}
              itemsCount={sortedSuperSimulations.length}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperSimulationsList;

//
// Utils
//

const renderSuperSimulationStatusButton = (
  superSimulation: SuperSimulation
) => {
  switch (superSimulation.status) {
    case "pending":
      return (
        <div className="flex items-center py-[5px]">
          <LoadingIcon className="animate-spin mr-2 h-4 w-4" />
          <span className="text-sm text-gray-500 font-medium dark:text-gray-50">
            Processing
          </span>
        </div>
      );
    case "completed":
      return (
        <Link
          to={`/locations/${superSimulation.location_uuid}/super-simulations/${superSimulation.uuid}/visible-hotspots`}
          className="inline-flex items-center px-2.5 py-1.5 border shadow-sm text-xs text-white font-medium rounded bg-hotspotty-400 border-hotspotty-500 hover:bg-hotspotty-500"
        >
          Open simulation
        </Link>
      );
    case "failed":
      return <span className="text-sm font-medium text-red-400">Failed</span>;
    default:
      return null;
  }
};
